<!--
  The part of the social feature that is not a screen.

  Two jobs, both of which have to happen whether or not you are looking at Friends:

  1. hand the store the tab's room socket through `attachSocial`;
  2. show the prompt when a friend asks to come in, and take you to their room when one
     says yes to you.

  MOUNTED ONCE, IN App.svelte, INSIDE THE INVITE GATE — so it is live on every screen
  including the watch screen, which is exactly when somebody is most likely to knock. It
  used to be mounted by Profile, Friends and Rooms, the three screens its author owned,
  and a friend asking to come in while you were watching a film reached nobody.

  Because it is the one app-wide social mount, it is also where the account is
  established (see the effect below) — that is a third job, and it belongs here for the
  same reason the other two do.

  ACCEPTING TAKES YOU THERE, AND DOES NOT WAIT TO BE ASKED TWICE. You pressed "Ask to
  join" less than a minute ago; a card saying "they said yes, press here" would be a
  second question for a decision you have already made. Being wrong about that costs one
  Back press, which the router handles.
-->
<script lang="ts">
  import { go, setCurrentRoom } from '$lib/ui/nav.svelte';
  import { untrack } from 'svelte';
  import JoinPrompt from './JoinPrompt.svelte';
  import { attachSocial } from './live';
  import { social } from './store';

  const store = social();
  const view = $derived($store);

  $effect(() => {
    let detach: (() => void) | null = null;
    let dead = false;
    void attachSocial(store).then((off) => {
      if (dead) off();
      else detach = off;
    });
    return () => {
      dead = true;
      detach?.();
    };
  });

  /**
   * ESTABLISH THE ACCOUNT ON FIRST LOAD, NOT ON FIRST VISIT TO A SETTINGS SCREEN.
   *
   * An account is minted by the first `GET /api/me`, and nothing called it unless the
   * user opened Profile, Friends or Rooms. The room socket takes its identity from that
   * account at the WebSocket upgrade — so somebody who only ever browsed and watched had
   * no account, recorded no room visits, and could not be found by handle by anybody in
   * the house. Measured on the deployed site: `social.visits` read 1 after roughly
   * thirty real room joins.
   *
   * Worse than empty, it was retroactively empty: the first time they opened Rooms, THAT
   * visit minted the account, and every evening already spent was gone for good.
   *
   * One request, once per tab, from the one component that is mounted app-wide inside
   * the invite gate. `load()` is idempotent, so the screens that already call it are
   * unaffected — and a service with no social layer answers 404, which `load()` records
   * as `unavailable` and nothing here retries.
   */
  $effect(() => {
    untrack(() => void store.load());
  });

  /**
   * A yes takes you in.
   *
   * TWO GUARDS, AND BOTH ARE LOAD-BEARING. `ask` is a `$derived` rather than a read of
   * `view` inside the effect, because `view` is the WHOLE snapshot — reading it here
   * would re-run this on every presence poll and every friends refresh. And the store
   * write is `untrack`ed for the reason docs/INTEGRATION.md gives: a mutator called from
   * an effect that also reads the state it changes re-runs for ever. The same shape,
   * unguarded, left Rooms spinning on its own history.
   */
  // Mailbox pushes must arrive while browsing or watching, not only on Friends.
  const ready = $derived(view.phase === 'ready');
  $effect(() => {
    if (!ready) return;
    return untrack(() => store.watchPresence());
  });

  const ask = $derived(view.outgoingAsk);
  $effect(() => {
    if (ask === null || ask.state !== 'accepted' || ask.roomId === null) return;
    const roomId = ask.roomId;
    untrack(() => {
      store.forgetAsk();
      setCurrentRoom(roomId);
      go(`/room/${encodeURIComponent(roomId)}`);
    });
  });
</script>

<JoinPrompt
  asks={view.incomingAsks}
  onanswer={(id, accept) => store.answerAsk(id, accept)}
  ontick={() => {
    store.expireIncoming();
    store.expireAsk();
  }}
/>
